import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, deleteVoicemail, getVoicemails, type Voicemail } from '../api';
import { callerOf, displayNumber, formatPhone } from '../phone/number';
import type { Phone } from '../phone/phone';
import { PhoneIcon, VoicemailIcon } from './icons';
import { ownNumberText } from '../phone/call-text';
import { VoicemailPlay } from './VoicemailPlay';

export interface VoicemailsState {
  items: Voicemail[];
  unheard: number;
  state: 'loading' | 'ready' | 'error';
  error: string | null;
  next: number | null;
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
  markHeard: (id: number) => void;
  remove: (id: number) => Promise<string | null>;
}

/** The user's voicemail messages; reloads when a new one arrives (live event). */
export function useVoicemails(phone: Phone): VoicemailsState {
  const [items, setItems] = useState<Voicemail[]>([]);
  const [unheard, setUnheard] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const req = useRef(0);
  const heardIds = useRef(new Set<number>()); // marked heard here already (count once)

  const reload = useCallback(async () => {
    const id = ++req.current;
    try {
      const r = await getVoicemails();
      if (id !== req.current) return;
      setItems(r.voicemails);
      setUnheard(r.unheard);
      setNext(r.nextBefore);
      setState('ready');
      setError(null);
    } catch (err) {
      if (id !== req.current) return;
      setError((err as Error).message);
      setState((s) => (s === 'ready' ? s : 'error'));
    }
  }, []);

  const loadMore = async () => {
    if (!next) return;
    try {
      const r = await getVoicemails(next);
      setItems((prev) => [...prev, ...r.voicemails.filter((v) => !prev.some((p) => p.id === v.id))]);
      setNext(r.nextBefore);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => phone.onVoicemailChange(() => void reload()), [phone, reload]);

  /** A message was played for the first time (from this list or from Recent calls). */
  const markHeard = (id: number) => {
    if (heardIds.current.has(id)) return;
    heardIds.current.add(id);
    setItems((prev) => prev.map((v) => (v.id === id ? { ...v, heard: true } : v)));
    setUnheard((n) => Math.max(0, n - 1));
  };

  const remove = async (id: number): Promise<string | null> => {
    try {
      await deleteVoicemail(id);
      const gone = items.find((v) => v.id === id);
      setItems((prev) => prev.filter((v) => v.id !== id));
      if (gone && !gone.heard) setUnheard((n) => Math.max(0, n - 1));
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Couldn’t delete it.';
    }
  };

  return { items, unheard, state, error, next, reload, loadMore, markHeard, remove };
}

export function VoicemailList({
  vm,
  canCall,
  onCallBack,
  onHeard,
  onDeleted,
  groupLabel,
  timeOf,
  severalNumbers = false,
  numberNames,
}: {
  vm: VoicemailsState;
  canCall: boolean;
  onCallBack: (n: string) => void;
  onHeard: (id: number) => void;
  onDeleted: (id: number) => void;
  groupLabel: (d: Date) => string;
  timeOf: (iso: string) => string;
  /** the user has several numbers: say which one each caller dialled (Numbers PR 1) */
  severalNumbers?: boolean;
  /** Numbers PR 2: the user's names for their numbers */
  numberNames?: Record<string, string>;
}) {
  const [confirm, setConfirm] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (vm.state === 'loading') return <p className="history-empty">Loading your voicemail…</p>;
  if (vm.state === 'error')
    return (
      <div className="history-empty">
        <p>Couldn’t load your voicemail. {vm.error}</p>
        <button className="btn btn-small" type="button" onClick={() => void vm.reload()}>
          Try again
        </button>
      </div>
    );
  if (!vm.items.length)
    return <p className="history-empty">No voicemail. When you don’t answer, callers can leave a message here.</p>;

  const days: Array<[string, Voicemail[]]> = [];
  for (const v of vm.items) {
    const label = groupLabel(new Date(v.createdAt));
    const last = days[days.length - 1];
    if (last && last[0] === label) last[1].push(v);
    else days.push([label, [v]]);
  }

  const del = async (id: number) => {
    if (confirm !== id) {
      setConfirm(id);
      return;
    }
    setConfirm(null);
    const err = await vm.remove(id);
    setMessage(err);
    if (!err) onDeleted(id);
  };

  return (
    <>
      {message && (
        <p className="setting-msg setting-msg-bad" role="alert">
          {message}
        </p>
      )}
      <ol className="calls">
        {days.map(([day, items]) => (
          <li key={day} className="calls-day">
            <h3>{day}</h3>
            <ol>
              {items.map((v) => {
                const who = callerOf(v.from, v.name);
                const own = ownNumberText(severalNumbers, 'inbound', v.to, numberNames);
                return (
                  <li key={v.id} className={`call vm-row${v.heard ? '' : ' vm-unheard'}`}>
                    <span className="call-dir" aria-hidden>
                      <VoicemailIcon />
                    </span>
                    <span className="call-main">
                      <span className="call-number">
                        <span className="call-who">{who.name ?? (displayNumber(who.number) || 'Unknown caller')}</span>
                      </span>
                      {((who.name && who.number) || own) && (
                        <span className="call-meta">
                          {who.name && who.number ? displayNumber(who.number) : ''}
                          {own ? <span className="call-from" title={v.to ? formatPhone(v.to) : undefined}>{who.name && who.number ? own : own.replace(/^ · /, '')}</span> : null}
                        </span>
                      )}
                      <span>
                        <VoicemailPlay
                          id={v.id}
                          durationSec={v.durationSec}
                          heard={v.heard}
                          label={v.heard ? 'Play' : 'New'}
                          onHeard={onHeard}
                        />
                        <button
                          type="button"
                          className={`vm-delete${confirm === v.id ? ' vm-confirm' : ''}`}
                          onClick={() => void del(v.id)}
                          onBlur={() => setConfirm((c) => (c === v.id ? null : c))}
                        >
                          {confirm === v.id ? 'Delete for good?' : 'Delete'}
                        </button>
                      </span>
                    </span>
                    <time className="call-time" dateTime={v.createdAt}>
                      {timeOf(v.createdAt)}
                    </time>
                    <button
                      type="button"
                      className="icon-btn call-again"
                      disabled={!canCall || !v.from}
                      aria-label={`Call ${v.from || 'back'}`}
                      title={`Call ${displayNumber(v.from)}`}
                      onClick={() => onCallBack(v.from)}
                    >
                      <PhoneIcon size={18} />
                    </button>
                  </li>
                );
              })}
            </ol>
          </li>
        ))}
      </ol>
      {vm.next && (
        <button className="btn btn-quiet btn-block" type="button" onClick={() => void vm.loadMore()}>
          Show older voicemail
        </button>
      )}
    </>
  );
}
